import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react";
import {
  Container,
  TextInput,
  Box,
  Stack,
  Text,
  Center,
  Title,
  Flex,
  Pagination,
  Badge,
  Paper,
  rem,
  useMantineTheme,
} from "@mantine/core";
import { MultiStepLoader } from "../../components/shared/MultiStepLoader";
import { useDebouncedValue, useMediaQuery } from "@mantine/hooks";
import { IconSearch, IconSparkles, IconBuilding } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";

import CompanyCardHorizontal, { CompanyWithUI } from "../../components/shared/CompanyCardHorizontal";
import InfoBanner from "../../components/shared/InfoBanner";
// import MultiSelectWithBadge from "../discover/Multiselectwithbadge ";
// import {
//   countriesData,
//   getIndustryOptions,
//   OPERATING_INDUSTRY_LABELS,
// } from "../../components/shared/constants/general.const";
import trpc from "../../shared/utils/trpc";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util.ts";
import DiscoverPromoCarousel, { type DiscoverPromoSlide } from "./DiscoverPromoCarousel";
// import FilterChips from "./FilterChips.tsx";

const PAGE_SIZE = 10;

/** Space under scrollable content so the last cards clear the fixed mobile promo strip ([ASBL-style bottom bar](https://asbl.in/blog/section-24-of-income-tax-act-explained-how-to-save-big-on-your-home-loan-2/)). */
const MOBILE_PROMO_STRIP_RESERVE = rem(200);

/** Viewport distance from bottom used to decide when to dock the strip after pagination (px). */
/** Approx. mobile strip height (paper vertical padding + carousel) for dock sentinel math. */
const MOBILE_PROMO_STRIP_PX = 150;

export type DiscoverPageProps = {
  /** When set and non-empty, replaces the default promo carousel data. UI stays in `DiscoverPromoCarousel`. */
  promoCarouselSlides?: DiscoverPromoSlide[];
};

export default function DiscoverPage({ promoCarouselSlides }: DiscoverPageProps) {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const isLgUp = useMediaQuery(`(min-width: ${theme.breakpoints.lg})`, false, { getInitialValueInEffect: true });

  /** Mobile: when true, promo sits in-flow after pagination; when false, fixed strip at viewport bottom. */
  const [mobileDocked, setMobileDocked] = useState(false);
  const mobileDockSentinelRef = useRef<HTMLDivElement>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchQuery, 700);
  const latestAiQueryRef = useRef<string>("");
  // const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  // const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [companies, setCompanies] = useState<CompanyWithUI[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isAiSearch, setIsAiSearch] = useState(false);
  const [matchCount, setMatchCount] = useState<number | null>(null);

  const MAX_RETRIES = 2;

  const defaultPromoSlides = useMemo(
    (): DiscoverPromoSlide[] => [
      {
        id: "promo-collab-india-china",
        flags: ["IN", "CN"],
        headline: "Recent collaboration",
        description: "Indian and Chinese collaboration made!",
      },
      { 
        id: "promo-collab-poland-vietnam",
        flags: ["PL", "VN"],
        headline: "Recent collaboration",
        description: "Poland and Vietnamese collaboration made!",
      },
      {
        id: "promo-collab-turkey-united-states",
        flags: ["TR", "US"],
        headline: "Recent collaboration",
        description: "Turkish and United States collaboration made!",
      },
    ],
    [],
  );

  const discoverPromoSlides =
    promoCarouselSlides && promoCarouselSlides.length > 0 ? promoCarouselSlides : defaultPromoSlides;

  // Accepts explicit page to avoid stale closure when called after page reset
  const fetchCompanies = async (pageToFetch = page) => {
    if (retryCount >= MAX_RETRIES) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(null);
    setMatchCount(null);

    try {
      const response = await trpc.company.listAll.query({
        page: pageToFetch,
        limit: PAGE_SIZE,
      });

      const mappedCompanies: CompanyWithUI[] = (response.companies || []).map((item: any) => ({
        ...item,
        isShortlisted: false,
      })) as CompanyWithUI[];

      setCompanies(mappedCompanies);
      setTotalPages(response.pagination?.totalPages || 1);
      setMatchCount(response.pagination?.total ?? mappedCompanies.length);
      setRetryCount(0);
    } catch (err) {
      handleTrpcError(err);
      const nextRetry = retryCount + 1;
      setRetryCount(nextRetry);
      if (nextRetry >= MAX_RETRIES) {
        setLoadError("Unable to load companies. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAiSearch = async (query: string) => {
    latestAiQueryRef.current = query;
    setLoading(true);
    setLoadError(null);
    setIsAiSearch(true);
    setMatchCount(null);

    try {
      const response = await trpc.company.searchAI.query({ query });

      if (latestAiQueryRef.current !== query) return;

      // Extract results from nested structure
      const results = response?.result?.data?.json?.results || response.results || response.companies || [];

      // Helper to ensure array fields are always arrays
      const ensureArray = (value: any): any[] => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        return [value];
      };

      const mappedCompanies: CompanyWithUI[] = results.map((item: any) => {
        const companyId = item.company_id || item.id;

        return {
          id: companyId,
          gtid: item.gtid,
          legalName: item.legal_name || item.legalName,
          tradeName: item.trade_name || item.tradeName,
          registrationNumber: item.registration_number || item.registrationNumber,
          yearOfIncorporation: item.year_of_incorporation || item.yearOfIncorporation,
          website: item.company_website || item.website,
          email: item.director_email || item.email,
          phoneCountryCode: item.phone_country_code || item.phoneCountryCode,
          phoneNumber: item.phone_number || item.phoneNumber,
          description: item.description,
          industryCategory: ensureArray(item.industry_category || item.industryCategory),
          subIndustry: ensureArray(item.sub_industry || item.subIndustry),
          collaborationIntent: ensureArray(item.collaboration_intent || item.collaborationIntent),
          coreCapabilities: ensureArray(item.core_capabilities || item.coreCapabilities),
          secondaryCapabilities: ensureArray(item.secondary_capabilities || item.secondaryCapabilities),
          keyDifferentiators: item.key_differentiators || item.keyDifferentiators,
          technologies: ensureArray(item.technologies_infrastructure || item.technologies),
          headquartersCountry: item.country || item.headquartersCountry,
          headquartersCity: item.state || item.headquartersCity,
          currentAddress: item.headquarters_location || item.currentAddress,
          operatingCountries: ensureArray(item.operating_countries || item.operatingCountries),
          preferredCollaborationRegions: ensureArray(item.preferred_collaboration_regions || item.preferredCollaborationRegions),
          status: item.status || "VERIFIED",
          isShortlisted: false,
        } as CompanyWithUI;
      });

      setCompanies(mappedCompanies);
      setTotalPages(1);
      setMatchCount(mappedCompanies.length);
    } catch (err) {
      if (latestAiQueryRef.current !== query) return;
      handleTrpcError(err);
      setLoadError("AI search failed. Please try again.");
    } finally {
      if (latestAiQueryRef.current === query) {
        setLoading(false);
      }
    }
  };

  // Triggers on search query change. Resets page and dispatches the right fetch.
  useEffect(() => {
    setPage(1);
    setCompanies([]);
    setRetryCount(0);
    setLoadError(null);

    if (debouncedSearch) {
      fetchAiSearch(debouncedSearch);
      return;
    }

    setIsAiSearch(false);
    void fetchCompanies(1);
  }, [debouncedSearch]);

  const handlePaginationChange = (nextPage: number) => {
    if (isAiSearch) return;
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
    void fetchCompanies(nextPage);
  };

  const updateMobileDocked = useCallback(() => {
    if (isLgUp) return;
    const el = mobileDockSentinelRef.current;
    if (!el) {
      setMobileDocked(true);
      return;
    }
    const top = el.getBoundingClientRect().top;
    setMobileDocked(top <= window.innerHeight - MOBILE_PROMO_STRIP_PX + 2);
  }, [isLgUp]);

  useLayoutEffect(() => {
    if (isLgUp) return undefined;
    updateMobileDocked();
    window.addEventListener("scroll", updateMobileDocked, { passive: true });
    window.addEventListener("resize", updateMobileDocked);
    return () => {
      window.removeEventListener("scroll", updateMobileDocked);
      window.removeEventListener("resize", updateMobileDocked);
    };
  }, [isLgUp, updateMobileDocked, companies.length, page, totalPages, loading, isAiSearch, loadError, debouncedSearch]);

  const mobilePromoBar = (
    <Box px={0} style={{ pointerEvents: "auto" }}>
      <Paper
        radius={0}
        withBorder={false}
        shadow="none"
        bg="transparent"
        p={5}
        w="100%"
        style={{
          overflow: "hidden",
        }}
      >
        <DiscoverPromoCarousel slides={discoverPromoSlides} height={70} autoplayDelayMs={4500} />
      </Paper>
    </Box>
  );

  return (
    <Box
      pb={{
        base: !isLgUp && !mobileDocked ? MOBILE_PROMO_STRIP_RESERVE : rem(8),
        lg: rem(40),
      }}
    >
      {/* Mobile Header */}
      <Box py="md" hiddenFrom="sm">
        <Container size="lg">
          <Title order={2} fz={20} fw={600}>
            Discover
          </Title>
          <Text size="sm" c="slate.7">
            Discover verified companies with advanced search criteria.
          </Text>
        </Container>
      </Box>

      <Container size="lg" py={{ base: 0, sm: 40 }}>
        <TextInput
          placeholder="Search by capability, industry, or keyword..."
          leftSection={<IconSearch size={18} />}
          size="md"
          radius="lg"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          mb="lg"
        />

        {/* Filters hidden — uncomment to restore
        <Group gap="md" align="flex-end" visibleFrom="sm">
          <MultiSelectWithBadge placeholder="Industry" data={industryOptions} value={selectedIndustries} onChange={setSelectedIndustries} leftSection={<IconBuilding size={18} />} width={148} searchable clearable />
          <MultiSelectWithBadge placeholder="Country" data={countryOptions} value={selectedCountries} onChange={setSelectedCountries} leftSection={<IconMapPin size={18} />} width={148} searchable clearable />
        </Group>
        <Box hiddenFrom="sm">
          <Group grow gap="sm">
            <MultiSelectWithBadge placeholder="Industry" data={industryOptions} value={selectedIndustries} onChange={setSelectedIndustries} leftSection={<IconBuilding size={18} />} searchable clearable />
            <MultiSelectWithBadge placeholder="Country" data={countryOptions} value={selectedCountries} onChange={setSelectedCountries} leftSection={<IconMapPin size={18} />} searchable clearable />
          </Group>
        </Box>
        <Flex mt="lg" mb="lg">
          <FilterChips value={activeFilters} labelMap={filterLabelMap} onRemove={handleRemoveFilter} />
        </Flex>
        */}

        <InfoBanner
          icon={IconSparkles}
          title="Sign up to unlock more"
          subtitle="Create an account to express interest, shortlist companies, and access full profiles."
        />

        {isAiSearch && (
          <Flex align="center" justify="space-between" wrap="wrap" gap="sm" mt="lg" mb="lg" w="100%">
            <Badge
              leftSection={<IconSparkles size={12} stroke={1.5} color="var(--mantine-color-violet-6)" />}
              color="violet"
              variant="light"
              size="sm"
              styles={{
                root: { textTransform: "uppercase", letterSpacing: "0.05em" },
                label: { fontWeight: 700, fontSize: 12 },
              }}
            >
              AI Search
            </Badge>
            <Badge
              leftSection={<IconBuilding size={12} stroke={1.5} color="var(--mantine-color-violet-6)" />}
              color="violet"
              variant="light"
              size="sm"
              styles={{
                root: { textTransform: "none" },
                label: { fontWeight: 700, fontSize: 12, letterSpacing: "0.02em" },
              }}
            >
              {loading && matchCount === null
                ? "…"
                : `${(matchCount ?? 0).toLocaleString()} ${matchCount === 1 ? "company" : "companies"}`}
            </Badge>
          </Flex>
        )}

        <Box style={{ position: "relative", minHeight: 200 }} mt={isAiSearch ? "md" : "lg"}>
          <MultiStepLoader
            loading={loading}
            inline
            minimal
            loadingStates={[
              { text: "Searching companies..." },
              { text: "Analyzing your query..." },
              { text: "Finding best matches..." },
              { text: "Ranking results..." },
            ]}
            title=""
            subtitle=""
            textSize="lg"
            duration={1500}
          />

          {companies.length === 0 && !loading ? (
            <Center py="xl">
              <Text c="slate.5">No companies found</Text>
            </Center>
          ) : (
            <Stack gap="md">
              {companies.map((company) => (
                <CompanyCardHorizontal
                  key={company.id}
                  company={company}
                  isDiscoverStyle
                  onShortlistClick={(id, isShortlisted) =>
                    setCompanies((prev) => prev.map((c) => (c.id === id ? { ...c, isShortlisted } : c)))
                  }
                  onViewDetails={(id) => navigate(`/discover/${id}`)}
                />
              ))}
            </Stack>
          )}
        </Box>

        {loadError && (
          <Center py="md">
            <Text c="red" size="sm">
              {loadError}
            </Text>
          </Center>
        )}

        {totalPages > 1 && !isAiSearch && (
          <Center mt="xl">
            <Pagination total={totalPages} value={page} onChange={handlePaginationChange} size="md" radius="md" />
          </Center>
        )}

        {/* Marks end of list + pagination; when it enters the strip zone, mobile promo docks in-flow below */}
        <div ref={mobileDockSentinelRef} style={{ height: 0, width: "100%" }} aria-hidden />

        {!isLgUp && mobileDocked ? (
          <Box component="aside" mt="lg" aria-label="Featured collaborations">
            {mobilePromoBar}
          </Box>
        ) : null}
      </Container>

      {/* Mobile: fixed strip until user scrolls to end, then promo is docked in Container above */}
      {!isLgUp && !mobileDocked ? (
        <Box
          pos="fixed"
          left={0}
          right={0}
          bottom={0}
          pb="max(0px, env(safe-area-inset-bottom, 0px))"
          style={{ zIndex: 40, pointerEvents: "none" }}
          aria-label="Featured collaborations"
        >
          {mobilePromoBar}
        </Box>
      ) : null}

      {isLgUp ? (
        <Box
          pos="fixed"
          bottom={rem(24)}
          right={rem(24)}
          w={rem(300)}
          style={{ zIndex: 40 }}
          aria-label="Featured collaborations"
        >
          <Paper
            radius="lg"
            p="xs"
            withBorder
            shadow="md"
            bg="primary.0"
            w="100%"
            style={{ overflow: "hidden" }}
          >
            <DiscoverPromoCarousel slides={discoverPromoSlides} height={168} autoplayDelayMs={4500} />
          </Paper>
        </Box>
      ) : null}
    </Box>
  );
}
